/**
 * PKCE (Proof Key for Code Exchange, RFC 7636) helpers.
 *
 * Pour OAuth Authorization Code flow sur public client (PWA) :
 *   1. Génère un code_verifier (random, ~43-128 chars)
 *   2. Dérive un code_challenge = base64url(SHA256(verifier))
 *   3. Envoie code_challenge à Google avec l'authorize request
 *   4. Au token exchange, envoie code_verifier — Google vérifie qu'il match
 *
 * Le code_verifier est gardé en sessionStorage entre l'authorize et le callback.
 */

/**
 * 32 bytes random → ~43 chars base64url. Spec: 43 to 128 chars,
 * doit être unique par flow OAuth.
 */
export async function generateCodeVerifier(): Promise<string> {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return base64UrlEncode(bytes);
}

/**
 * code_challenge = base64url(SHA256(code_verifier)).
 * Méthode S256 (vs `plain` qui est insécure → on n'utilise jamais plain).
 */
export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

/**
 * Random state pour CSRF protection. 16 bytes random suffisent.
 * Vérifié au callback : doit match ce qu'on a envoyé à l'authorize.
 */
export function generateState(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return base64UrlEncode(bytes);
}

function base64UrlEncode(bytes: Uint8Array): string {
  let str = "";
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}
